"use strict";
// The document template binder. It lives in the shared package rather than under a backend
// feature because features never import from each other, and because the same binder has to serve
// both the issued render and any later preview surface.
//
// The language is deliberately tiny: `{{path.to.value}}` for a scalar,
// `{{#if path}}…{{/if}}` to omit a block when the path is absent or empty,
// `{{#each path}}…{{/each}}` (and legacy `{{#name}}…{{/name}}`) to repeat a block,
// and `{{this}}` for the current item of a string list. It has no else, no helpers,
// no expressions and no arithmetic, so a template can never compute a figure that
// disagrees with the frozen payload it was bound against.
Object.defineProperty(exports, "__esModule", { value: true });
exports.TrustedHtml = void 0;
exports.trustedHtml = trustedHtml;
exports.escapeHtml = escapeHtml;
exports.extractTemplateVariables = extractTemplateVariables;
exports.bindTemplate = bindTemplate;
// Marker for context values a server-side builder already rendered as HTML. The binder inserts
// these verbatim instead of escaping them.
//
// Safety contract: only construct TrustedHtml inside a server-side context builder, never from
// operator or template input. The class is checked by `instanceof`, not by shape, so a value
// revived from JSON can never impersonate it.
class TrustedHtml {
    html;
    constructor(html) {
        this.html = html;
    }
}
exports.TrustedHtml = TrustedHtml;
function trustedHtml(html) {
    return new TrustedHtml(html);
}
// Escapes a value interpolated into a pre-built TrustedHtml block. The binder escapes plain
// scalars on its own, so a server-built fragment must escape its own interpolated values.
function escapeHtml(value) {
    return value
        .replace(/&/gu, '&amp;')
        .replace(/</gu, '&lt;')
        .replace(/>/gu, '&gt;')
        .replace(/"/gu, '&quot;')
        .replace(/'/gu, '&#39;');
}
const VARIABLE_PATTERN = /\{\{([\w.]+)\}\}/g;
const SECTION_PATTERN = /\{\{#([\w.]+)\}\}/g;
const IF_EACH_PATTERN = /\{\{#(?:if|each)\s+([\w.]+)\}\}/g;
const OPTIONAL_LINE_PATTERN = /<([a-z]+)\s+class\s*=\s*["']optional-line["']\s*>\s*<\/\1>/gi;
// Collects the top-level context keys a template references, reduced to the root before any dot.
function extractTemplateVariables(template) {
    const names = new Set();
    for (const match of [
        ...template.matchAll(VARIABLE_PATTERN),
        ...template.matchAll(SECTION_PATTERN),
        ...template.matchAll(IF_EACH_PATTERN),
    ]) {
        const path = match[1];
        if (path !== undefined) {
            names.add(path.split('.')[0] ?? path);
        }
    }
    return names;
}
function bindTemplate(template, context, mode = 'production') {
    const unresolved = [];
    const html = stripEmptyOptionalLines(expandAndBind(template, context, { mode, unresolved }));
    return { html, unresolvedPaths: unresolved };
}
function expandAndBind(template, context, pass) {
    let result = '';
    let cursor = 0;
    while (cursor < template.length) {
        const openStart = template.indexOf('{{#', cursor);
        if (openStart === -1) {
            result += bindVariablesInString(template.slice(cursor), context, pass);
            break;
        }
        result += bindVariablesInString(template.slice(cursor, openStart), context, pass);
        const open = parseOpenTag(template, openStart);
        if (open === null) {
            result += bindVariablesInString(template.slice(openStart), context, pass);
            break;
        }
        const close = findMatchingClose(template, open.tagEnd, open);
        if (close === null) {
            result += bindVariablesInString(template.slice(openStart), context, pass);
            break;
        }
        const body = template.slice(open.tagEnd, close.closeStart);
        result += expandBlock(open, body, context, pass);
        cursor = close.closeEnd;
    }
    return result;
}
function expandBlock(open, body, context, pass) {
    if (open.kind === 'if') {
        if (isTruthy(resolvePath(context, open.path))) {
            return expandAndBind(body, context, pass);
        }
        return '';
    }
    const items = resolveArray(context, open.path);
    if (items === null) {
        pass.unresolved.push(open.path);
        return pass.mode === 'preview' ? `[unknown section: ${open.path}]` : '';
    }
    return items.map((item) => expandAndBind(body, eachItemContext(item), pass)).join('');
}
function parseOpenTag(template, openStart) {
    const nameEnd = template.indexOf('}}', openStart + 3);
    if (nameEnd === -1) {
        return null;
    }
    const inner = template
        .slice(openStart + 3, nameEnd)
        .replace(/\s+/g, ' ')
        .trim();
    const tagEnd = nameEnd + 2;
    if (inner === 'if' || inner.startsWith('if ')) {
        return { kind: 'if', path: inner.slice(2).trim(), tagEnd };
    }
    if (inner === 'each' || inner.startsWith('each ')) {
        return { kind: 'each', path: inner.slice(4).trim(), tagEnd };
    }
    return { kind: 'section', path: inner, tagEnd };
}
function parseCloseTag(template, closeStart) {
    const nameEnd = template.indexOf('}}', closeStart + 3);
    if (nameEnd === -1) {
        return null;
    }
    const inner = template.slice(closeStart + 3, nameEnd).trim();
    const tagEnd = nameEnd + 2;
    if (inner === 'if') {
        return { kind: 'if', name: 'if', tagEnd };
    }
    if (inner === 'each') {
        return { kind: 'each', name: 'each', tagEnd };
    }
    return { kind: 'section', name: inner, tagEnd };
}
function findMatchingClose(template, from, open) {
    const stack = [open];
    let cursor = from;
    while (cursor < template.length) {
        const nextOpenStart = template.indexOf('{{#', cursor);
        const nextCloseStart = template.indexOf('{{/', cursor);
        if (nextCloseStart === -1) {
            return null;
        }
        if (nextOpenStart !== -1 && nextOpenStart < nextCloseStart) {
            const nested = parseOpenTag(template, nextOpenStart);
            if (nested === null) {
                return null;
            }
            stack.push({ kind: nested.kind, path: nested.path });
            cursor = nested.tagEnd;
            continue;
        }
        const close = parseCloseTag(template, nextCloseStart);
        if (close === null) {
            return null;
        }
        const top = stack[stack.length - 1];
        if (top === undefined || !closes(top, close)) {
            return null;
        }
        stack.pop();
        if (stack.length === 0) {
            return { closeStart: nextCloseStart, closeEnd: close.tagEnd };
        }
        cursor = close.tagEnd;
    }
    return null;
}
function closes(open, close) {
    if (open.kind !== close.kind) {
        return false;
    }
    return open.kind !== 'section' || open.path === close.name;
}
function bindVariablesInString(source, context, pass) {
    return source.replace(VARIABLE_PATTERN, (_match, path) => {
        const resolved = resolvePath(context, path);
        if (resolved === undefined) {
            pass.unresolved.push(path);
            return pass.mode === 'preview' ? `[unknown variable: ${path}]` : '';
        }
        if (resolved === null) {
            return '';
        }
        if (resolved instanceof TrustedHtml) {
            return resolved.html;
        }
        return escapeHtml(formatValue(resolved));
    });
}
function resolvePath(context, path) {
    return path.split('.').reduce((acc, key) => {
        if (acc == null || typeof acc !== 'object') {
            return undefined;
        }
        return acc[key];
    }, context);
}
function resolveArray(context, path) {
    const value = resolvePath(context, path);
    if (!Array.isArray(value)) {
        return null;
    }
    const items = [];
    for (const item of value) {
        items.push(item);
    }
    return items;
}
function eachItemContext(item) {
    if (item !== null && typeof item === 'object') {
        return { ...item, this: item };
    }
    return { this: item };
}
// `{}` is falsy so an omitted structured block can be `{}` or null, unlike JS `if`.
function isTruthy(value) {
    if (value === undefined || value === null || value === false || value === '' || value === 0) {
        return false;
    }
    if (value instanceof TrustedHtml) {
        return value.html !== '';
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (typeof value === 'object') {
        return Object.keys(value).length > 0;
    }
    return true;
}
// Dates never reach the binder as Date objects on the issued path: the render model carries
// already-formatted date strings, so a render can never depend on the host's time zone.
function formatValue(value) {
    if (typeof value === 'number' || typeof value === 'bigint' || typeof value === 'boolean') {
        return value.toString();
    }
    if (typeof value === 'string') {
        return value;
    }
    return String(value);
}
function stripEmptyOptionalLines(html) {
    return html.replace(OPTIONAL_LINE_PATTERN, '');
}
//# sourceMappingURL=template-binding.js.map